import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_CONFIG } from '../config/apiConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));

  // Check authentication on app load
  useEffect(() => {
    const checkAuth = async () => {
      const storedAccessToken = localStorage.getItem('access_token');

      if (storedAccessToken) {
        try {
          const response = await fetch(API_CONFIG.auth.user(), {
            headers: {
              'Authorization': `Bearer ${storedAccessToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            setAccessToken(storedAccessToken);
          } else if (response.status === 401) {
            // Try to refresh the token
            const refreshed = await refreshAccessToken();
            if (!refreshed) {
              clearTokens();
            }
          } else {
            clearTokens();
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          clearTokens();
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const clearTokens = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
  };

  const refreshAccessToken = async () => {
    const storedRefreshToken = localStorage.getItem('refresh_token');
    if (!storedRefreshToken) {
      return false;
    }

    try {
      const response = await fetch(API_CONFIG.auth.refresh(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: storedRefreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        const newAccessToken = data.access_token;

        localStorage.setItem('access_token', newAccessToken);
        setAccessToken(newAccessToken);

        return true;
      } else {
        clearTokens();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearTokens();
      return false;
    }
  };

  const login = async (email, password) => {
    console.log('🔐 SimpleAuthContext: Starting login process...');

    // Try direct backend connection first
    // Use API_CONFIG to get the full URL, avoiding proxy issues
    const backendUrl = API_CONFIG.auth.login();
    console.log('🔐 SimpleAuthContext: Request URL will be:', backendUrl);
    console.log('🔐 SimpleAuthContext: Current window location:', window.location.href);

    try {
      console.log('🔐 SimpleAuthContext: Attempting login with direct backend URL');

      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      console.log('🔐 SimpleAuthContext: Response status:', response.status);
      console.log('🔐 SimpleAuthContext: Response headers:', [...response.headers.entries()]);

      if (response.ok) {
        const data = await response.json();
        console.log('🔐 SimpleAuthContext: Login successful:', data);
        const { access_token, refresh_token, user: userData } = data;

        setAccessToken(access_token);
        setRefreshToken(refresh_token);

        // Debug logging for user data
        if (process.env.NODE_ENV === 'development') {
          console.log('🔐 SimpleAuthContext: Setting user data:', userData);
          console.log('🔐 SimpleAuthContext: User profile picture:', userData?.profile_picture);
        }

        setUser(userData);
        localStorage.setItem('access_token', access_token);
        localStorage.setItem('refresh_token', refresh_token);

        return { success: true };
      } else {
        console.log('🔐 SimpleAuthContext: Login failed with status:', response.status);
        let errorData;
        try {
          errorData = await response.json();
          console.log('🔐 SimpleAuthContext: Error response data:', errorData);
        } catch (e) {
          // If response is not JSON (like HTML error page), handle it
          const textResponse = await response.text();
          console.log('🔐 SimpleAuthContext: Non-JSON response:', textResponse.substring(0, 200));
          return {
            success: false,
            error: `Server error (${response.status}). Please try again.`
          };
        }

        // Extract detailed error message
        let errorMessage = 'Login failed';
        if (errorData.errors) {
          // Handle validation errors from serializer
          const errors = errorData.errors;
          if (errors.email) {
            errorMessage = `Email: ${errors.email.join(', ')}`;
          } else if (errors.password) {
            errorMessage = `Password: ${errors.password.join(', ')}`;
          } else if (errors.non_field_errors) {
            errorMessage = errors.non_field_errors.join(', ');
          }
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        }

        return {
          success: false,
          error: errorMessage
        };
      }
    } catch (error) {
      console.error('🔐 SimpleAuthContext: Login error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.'
      };
    }
  };

  const register = async (userData) => {
    console.log('🔐 SimpleAuthContext: Starting registration process...');
    const backendUrl = API_CONFIG.auth.register();

    try {
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔐 SimpleAuthContext: Registration successful:', data);
        return { success: true, data };
      } else {
        console.log('🔐 SimpleAuthContext: Registration failed with status:', response.status);
        let errorData;
        try {
          errorData = await response.json();
          console.log('🔐 SimpleAuthContext: Error response data:', errorData);
        } catch (e) {
          const textResponse = await response.text();
          return {
            success: false,
            error: `Server error (${response.status}). Please try again.`
          };
        }

        return {
          success: false,
          error: errorData.detail || errorData.message || 'Registration failed',
          errors: errorData.errors || errorData
        };
      }
    } catch (error) {
      console.error('🔐 SimpleAuthContext: Registration error:', error);
      return {
        success: false,
        error: 'Network error. Please try again.'
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to blacklist refresh token
      const storedRefreshToken = localStorage.getItem('refresh_token');
      if (storedRefreshToken) {
        await fetch(API_CONFIG.auth.logout(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refresh_token: storedRefreshToken }),
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    clearTokens();
  };

  const makeAuthenticatedRequest = async (url, options = {}) => {
    let currentAccessToken = accessToken || localStorage.getItem('access_token');

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // If sending FormData, let the browser set the Content-Type header with the boundary
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    if (currentAccessToken) {
      headers['Authorization'] = `Bearer ${currentAccessToken}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // If we get a 401, try to refresh the token
      if (response.status === 401 && refreshToken) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Retry the request with the new token
          const newAccessToken = localStorage.getItem('access_token');
          headers['Authorization'] = `Bearer ${newAccessToken}`;
          return fetch(url, {
            ...options,
            headers,
          });
        }
      }

      return response;
    } catch (error) {
      console.error('Authenticated request failed:', error);
      throw error;
    }
  };

  const value = {
    user,
    accessToken,
    refreshToken,
    loading,
    login,
    register,
    logout,
    makeAuthenticatedRequest,
    refreshAccessToken,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
